import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Tabs,
    Tab,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Avatar,
    Snackbar,
    Switch,
    FormControlLabel,
    CircularProgress,
    Radio,
    InputAdornment,
    alpha,
    Chip,
} from '@mui/material';
import {
    Edit as EditIcon,
    Add as AddIcon,
    Save as SaveIcon,
    PhotoCamera as PhotoCameraIcon,
    Person as PersonIcon,
    Visibility,
    VisibilityOff,
    AdminPanelSettings as AdminIcon,
    AccountCircle as AccountCircleIcon,
    Settings as SettingsIcon,
    CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { usersAPI, User, getImageUrl, systemSettingsAPI, superAdminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ImageCrop from '../../components/ImageCrop';

// Dark OLED Palette (shared with dashboard)
const PALETTE = {
    bg: '#020617',
    surface: '#0F172A',
    surfaceAlt: '#1E293B',
    cta: '#22C55E',
    ctaDim: 'rgba(34, 197, 94, 0.15)',
    text: '#F8FAFC',
    textDim: '#94A3B8',
    border: 'rgba(248, 250, 252, 0.06)',
    borderHover: 'rgba(248, 250, 252, 0.12)',
    accent: '#8B5CF6',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
};

// Styled TextField for dark mode
const darkTextFieldSx = {
    '& .MuiOutlinedInput-root': {
        fontFamily: 'Vazir, sans-serif',
        color: PALETTE.text,
        height: 48,
        borderRadius: '12px',
        backgroundColor: alpha('#1E293B', 0.6),
        '& fieldset': {
            borderColor: PALETTE.border,
        },
        '&:hover fieldset': {
            borderColor: PALETTE.borderHover,
        },
        '&.Mui-focused fieldset': {
            borderColor: PALETTE.cta,
            borderWidth: '1.5px',
        },
    },
    '& .MuiInputLabel-root': {
        fontFamily: 'Vazir, sans-serif',
        color: PALETTE.textDim,
        '&.Mui-focused': {
            color: PALETTE.cta,
        },
    },
    '& .MuiFormHelperText-root': {
        fontFamily: 'Vazir, sans-serif',
        color: PALETTE.textDim,
    },
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const SuperAdminSettings: React.FC = () => {
    const { user, setUser } = useAuth();
    const [tabValue, setTabValue] = useState(0);
    const [managers, setManagers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Managers Tab State
    const [openModal, setOpenModal] = useState(false);
    const [editingManager, setEditingManager] = useState<User | null>(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        mobile: '',
        password: '',
        avatarUrl: '',
        isActive: true
    });
    const [croppingTarget, setCroppingTarget] = useState<'profile' | 'manager'>('profile');

    // Profile Tab State
    const [profileData, setProfileData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        mobile: user?.mobile || '',
        password: '',
        confirmPassword: '',
    });
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [showCrop, setShowCrop] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showAdminPassword, setShowAdminPassword] = useState(false);

    // Global Settings Tab State
    const [excelCurrency, setExcelCurrency] = useState<'toman' | 'rial'>('toman');

    useEffect(() => {
        if (tabValue === 0) {
            fetchManagers();
        } else if (tabValue === 2) {
            fetchSystemSettings();
        }
    }, [tabValue]);

    const fetchSystemSettings = async () => {
        try {
            const data = await systemSettingsAPI.getSystemSettings();
            if (data && data.excelCurrency) {
                setExcelCurrency(data.excelCurrency as 'toman' | 'rial');
            }
        } catch (err) {
            setError('خطا در دریافت تنظیمات سیستم');
        }
    };

    const handleSaveSystemSettings = async () => {
        setLoading(true);
        try {
            await systemSettingsAPI.updateSystemSettings({ excelCurrency });
            setSuccess('تنظیمات با موفقیت ذخیره شد');
        } catch (err) {
            setError('خطا در ذخیره تنظیمات');
        } finally {
            setLoading(false);
        }
    };

    const fetchManagers = async () => {
        setLoading(true);
        try {
            const data = await usersAPI.getUsers('superadmin');
            setManagers(data);
        } catch (err) {
            setError('خطا در دریافت لیست مدیران سیستم');
        } finally {
            setLoading(false);
        }
    };

    // Keep profile data in sync with user context
    useEffect(() => {
        if (user) {
            setProfileData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                mobile: user.mobile || '',
                password: '',
                confirmPassword: ''
            });
        }
    }, [user]);

    const handleOpenModal = (manager?: User) => {
        if (manager) {
            setEditingManager(manager);
            setFormData({
                firstName: manager.firstName,
                lastName: manager.lastName,
                mobile: manager.mobile,
                password: '',
                avatarUrl: manager.avatarUrl || '',
                isActive: manager.isActive !== false
            });
        } else {
            setEditingManager(null);
            setFormData({
                firstName: '',
                lastName: '',
                mobile: '',
                password: '',
                avatarUrl: '',
                isActive: true
            });
        }
        setOpenModal(true);
    };

    const handleSaveManager = async () => {
        try {
            if (editingManager) {
                const response = await usersAPI.updateUser(editingManager.id, {
                    ...formData,
                    role: 'superadmin'
                });
                
                // If editing self, update the context
                if (user && editingManager.id === user.id && setUser) {
                    setUser({
                        ...user,
                        firstName: response.firstName,
                        lastName: response.lastName,
                        mobile: response.mobile,
                        avatarUrl: response.avatarUrl || user.avatarUrl
                    });
                }
                
                setSuccess('اطلاعات مدیر با موفقیت به‌روزرسانی شد');
            } else {
                if (!formData.password) {
                    setError('رمز عبور برای مدیر جدید الزامی است');
                    return;
                }
                await usersAPI.createUser({
                    ...formData,
                    role: 'superadmin'
                });
                setSuccess('مدیر جدید با موفقیت ایجاد شد');
            }
            setOpenModal(false);
            fetchManagers();
        } catch (err: any) {
            setError(err.response?.data?.error || 'خطا در ذخیره اطلاعات');
        }
    };

    const handleUpdateProfile = async () => {
        if (profileData.password && profileData.password !== profileData.confirmPassword) {
            setError('رمز عبور و تکرار آن مطابقت ندارند');
            return;
        }

        setLoading(true);
        try {
            const result = await usersAPI.updateUser(user!.id, {
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                mobile: profileData.mobile,
                password: profileData.password || undefined,
            });
            
            // Update context
            if (setUser) {
                setUser({
                    ...user!,
                    firstName: result.firstName,
                    lastName: result.lastName,
                    mobile: result.mobile,
                });
            }
            setSuccess('پروفایل با موفقیت به‌روزرسانی شد');
            setProfileData(prev => ({ ...prev, password: '', confirmPassword: '' }));
        } catch (err: any) {
            setError(err.response?.data?.error || 'خطا در به‌روزرسانی پروفایل');
        } finally {
            setLoading(false);
        }
    };

    const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>, target: 'profile' | 'manager') => {
        if (e.target.files && e.target.files[0]) {
            setCroppingTarget(target);
            setAvatarFile(e.target.files[0]);
            setShowCrop(true);
        }
    };

    const onCropComplete = async (blob: Blob) => {
        setUploading(true);
        try {
            const targetId = croppingTarget === 'profile' ? user?.id : editingManager?.id;
            const url = await usersAPI.uploadAvatar(blob, targetId);
            
            if (croppingTarget === 'profile' && setUser) {
                setUser({ ...user!, avatarUrl: url });
            } else if (croppingTarget === 'manager') {
                setFormData(prev => ({ ...prev, avatarUrl: url }));
            }
            
            setSuccess('تصویر پروفایل با موفقیت آپلود شد');
        } catch (err: any) {
            setError(err.message || 'خطا در آپلود تصویر');
        } finally {
            setUploading(false);
            setShowCrop(false);
            setAvatarFile(null);
        }
    };

    const tabs = [
        { label: 'مدیران ارشد', icon: <AdminIcon sx={{ fontSize: 18 }} /> },
        { label: 'پروفایل', icon: <AccountCircleIcon sx={{ fontSize: 18 }} /> },
        { label: 'تنظیمات گزارشات', icon: <SettingsIcon sx={{ fontSize: 18 }} /> },
        { label: 'تقویم مناسبت‌ها', icon: <CalendarIcon sx={{ fontSize: 18 }} /> },
    ];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '14px',
                    background: `linear-gradient(135deg, ${PALETTE.accent} 0%, #6D28D9 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 0 20px ${alpha(PALETTE.accent, 0.25)}`,
                }}>
                    <SettingsIcon sx={{ color: '#fff', fontSize: 26 }} />
                </Box>
                <Box>
                    <Typography variant="h5" sx={{
                        fontFamily: 'Vazir, sans-serif',
                        fontWeight: 800,
                        color: PALETTE.text,
                        letterSpacing: '-0.5px',
                    }}>
                        تنظیمات کلان سامانه
                    </Typography>
                    <Typography sx={{
                        fontFamily: 'Vazir, sans-serif',
                        fontSize: '0.85rem',
                        color: PALETTE.textDim,
                        mt: 0.25,
                    }}>
                        مدیریت مدیران ارشد، پروفایل و تنظیمات سیستمی
                    </Typography>
                </Box>
            </Box>

            {/* Main Card */}
            <Card sx={{
                background: PALETTE.surface,
                border: `1px solid ${PALETTE.border}`,
                borderRadius: '16px',
                overflow: 'visible',
            }}>
                <Box sx={{ borderBottom: `1px solid ${PALETTE.border}` }}>
                    <Tabs
                        value={tabValue}
                        onChange={(_, v) => setTabValue(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 56,
                            '& .MuiTab-root': {
                                fontFamily: 'Vazir, sans-serif',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                color: PALETTE.textDim,
                                minHeight: 56,
                                textTransform: 'none',
                                transition: 'color 0.2s',
                                gap: 0.75,
                                '&.Mui-selected': {
                                    color: PALETTE.cta,
                                },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: PALETTE.cta,
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                            },
                        }}
                    >
                        {tabs.map((tab, i) => (
                            <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
                        ))}
                    </Tabs>
                </Box>

                {/* Tab 0: Managers */}
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenModal()}
                            sx={{
                                fontFamily: 'Vazir, sans-serif',
                                fontWeight: 600,
                                height: 48,
                                borderRadius: '12px',
                                background: `linear-gradient(135deg, ${PALETTE.cta} 0%, #16A34A 100%)`,
                                boxShadow: `0 4px 14px ${alpha(PALETTE.cta, 0.3)}`,
                                textTransform: 'none',
                                px: 3,
                                '&:hover': {
                                    boxShadow: `0 6px 20px ${alpha(PALETTE.cta, 0.4)}`,
                                },
                            }}
                        >
                            افزودن مدیر جدید
                        </Button>
                    </Box>

                    <TableContainer sx={{
                        borderRadius: '12px',
                        border: `1px solid ${PALETTE.border}`,
                        background: alpha(PALETTE.surfaceAlt, 0.3),
                    }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {['نام و نام خانوادگی', 'شماره موبایل', 'وضعیت', 'عملیات'].map((h) => (
                                        <TableCell key={h} sx={{
                                            fontFamily: 'Vazir, sans-serif',
                                            fontWeight: 700,
                                            fontSize: '0.82rem',
                                            color: PALETTE.textDim,
                                            borderBottom: `1px solid ${PALETTE.border}`,
                                            background: alpha(PALETTE.surfaceAlt, 0.5),
                                            py: 1.5,
                                        }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && managers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ borderBottom: `1px solid ${PALETTE.border}`, py: 4 }}>
                                            <CircularProgress size={24} sx={{ color: PALETTE.cta }} />
                                        </TableCell>
                                    </TableRow>
                                ) : managers.map((m) => (
                                    <TableRow
                                        key={m.id}
                                        sx={{
                                            transition: 'background 0.2s',
                                            '&:hover': { background: alpha(PALETTE.text, 0.02) },
                                        }}
                                    >
                                        <TableCell sx={{
                                            fontFamily: 'Vazir, sans-serif',
                                            color: PALETTE.text,
                                            fontWeight: 600,
                                            borderBottom: `1px solid ${PALETTE.border}`,
                                            py: 1.75,
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar
                                                    src={getImageUrl(m.avatarUrl)}
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        bgcolor: alpha(PALETTE.accent, 0.15),
                                                        border: `2px solid ${alpha(PALETTE.accent, 0.2)}`,
                                                    }}
                                                >
                                                    <PersonIcon sx={{ fontSize: 18, color: PALETTE.accent }} />
                                                </Avatar>
                                                {m.firstName} {m.lastName}
                                            </Box>
                                        </TableCell>
                                        <TableCell dir="ltr" sx={{
                                            fontFamily: 'Vazir, sans-serif',
                                            color: PALETTE.textDim,
                                            borderBottom: `1px solid ${PALETTE.border}`,
                                            py: 1.75,
                                        }}>
                                            {m.mobile}
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: `1px solid ${PALETTE.border}`, py: 1.75 }}>
                                            <Chip
                                                label={m.isActive ? 'فعال' : 'غیرفعال'}
                                                size="small"
                                                sx={{
                                                    fontFamily: 'Vazir, sans-serif',
                                                    height: 24,
                                                    fontSize: '0.72rem',
                                                    fontWeight: 600,
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    background: m.isActive
                                                        ? alpha(PALETTE.cta, 0.12)
                                                        : alpha(PALETTE.error, 0.12),
                                                    color: m.isActive ? PALETTE.cta : PALETTE.error,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: `1px solid ${PALETTE.border}`, py: 1.75 }}>
                                            <IconButton
                                                onClick={() => handleOpenModal(m)}
                                                size="small"
                                                sx={{
                                                    color: PALETTE.info,
                                                    background: alpha(PALETTE.info, 0.1),
                                                    borderRadius: '10px',
                                                    width: 36,
                                                    height: 36,
                                                    '&:hover': {
                                                        background: alpha(PALETTE.info, 0.2),
                                                    },
                                                }}
                                            >
                                                <EditIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Tab 1: Profile */}
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Avatar
                                    src={getImageUrl(user?.avatarUrl)}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        mb: 2,
                                        border: `3px solid ${alpha(PALETTE.accent, 0.3)}`,
                                        boxShadow: `0 0 24px ${alpha(PALETTE.accent, 0.15)}`,
                                    }}
                                >
                                    <PersonIcon sx={{ fontSize: 60, color: PALETTE.textDim }} />
                                </Avatar>
                                <IconButton
                                    component="label"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 15,
                                        right: 0,
                                        width: 36,
                                        height: 36,
                                        background: `linear-gradient(135deg, ${PALETTE.accent} 0%, #6D28D9 100%)`,
                                        color: 'white',
                                        '&:hover': { background: `linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)` },
                                    }}
                                    disabled={uploading}
                                >
                                    <input type="file" hidden accept="image/*" onChange={(e) => handleAvatarSelect(e, 'profile')} />
                                    <PhotoCameraIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            {uploading && <CircularProgress size={20} sx={{ mt: 1, color: PALETTE.cta }} />}
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                            <TextField
                                label="نام"
                                fullWidth
                                value={profileData.firstName}
                                onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                                sx={darkTextFieldSx}
                            />
                            <TextField
                                label="نام خانوادگی"
                                fullWidth
                                value={profileData.lastName}
                                onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                                sx={darkTextFieldSx}
                            />
                        </Box>

                        <TextField
                            label="شماره موبایل"
                            fullWidth
                            sx={{ ...darkTextFieldSx, mb: 3 }}
                            value={profileData.mobile}
                            onChange={(e) => setProfileData({ ...profileData, mobile: e.target.value })}
                            dir="ltr"
                            autoComplete="off"
                        />

                        <Typography sx={{
                            fontFamily: 'Vazir, sans-serif',
                            fontSize: '0.82rem',
                            color: PALETTE.textDim,
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                            <Box sx={{
                                width: 4,
                                height: 16,
                                borderRadius: 2,
                                background: PALETTE.warning,
                            }} />
                            تغییر رمز عبور (در صورت نیاز)
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                            <TextField
                                label="رمز عبور جدید"
                                type={showNewPassword ? 'text' : 'password'}
                                fullWidth
                                value={profileData.password}
                                onChange={(e) => setProfileData({ ...profileData, password: e.target.value })}
                                autoComplete="new-password"
                                sx={darkTextFieldSx}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="نمایش رمز عبور"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                edge="end"
                                                size="small"
                                                sx={{ color: PALETTE.textDim }}
                                            >
                                                {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                label="تکرار رمز عبور"
                                type={showConfirmPassword ? 'text' : 'password'}
                                fullWidth
                                value={profileData.confirmPassword}
                                onChange={(e) => setProfileData({ ...profileData, confirmPassword: e.target.value })}
                                sx={darkTextFieldSx}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="نمایش رمز عبور"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                                size="small"
                                                sx={{ color: PALETTE.textDim }}
                                            >
                                                {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            startIcon={<SaveIcon />}
                            onClick={handleUpdateProfile}
                            disabled={loading}
                            sx={{
                                fontFamily: 'Vazir, sans-serif',
                                height: 48,
                                borderRadius: '12px',
                                fontWeight: 600,
                                background: `linear-gradient(135deg, ${PALETTE.cta} 0%, #16A34A 100%)`,
                                boxShadow: `0 4px 14px ${alpha(PALETTE.cta, 0.3)}`,
                                textTransform: 'none',
                                '&:hover': {
                                    boxShadow: `0 6px 20px ${alpha(PALETTE.cta, 0.4)}`,
                                },
                            }}
                        >
                            ذخیره تغییرات پروفایل
                        </Button>
                    </Box>
                </TabPanel>

                {/* Tab 2: Report Settings */}
                <TabPanel value={tabValue} index={2}>
                    <Box sx={{ maxWidth: 500, mx: 'auto' }}>
                        <Box sx={{
                            p: 3,
                            borderRadius: '14px',
                            border: `1px solid ${PALETTE.border}`,
                            background: alpha(PALETTE.surfaceAlt, 0.3),
                            mb: 3,
                        }}>
                            <Typography sx={{
                                fontFamily: 'Vazir, sans-serif',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                color: PALETTE.text,
                                mb: 0.5,
                            }}>
                                واحد پول در خروجی اکسل
                            </Typography>
                            <Typography sx={{
                                fontFamily: 'Vazir, sans-serif',
                                fontSize: '0.78rem',
                                color: PALETTE.textDim,
                                mb: 3,
                            }}>
                                واحد پول پیش‌فرض برای نمایش در گزارشات مالی و خروجی اکسل
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                {(['toman', 'rial'] as const).map((opt) => (
                                    <Box
                                        key={opt}
                                        onClick={() => setExcelCurrency(opt)}
                                        sx={{
                                            flex: 1,
                                            p: 2,
                                            borderRadius: '12px',
                                            border: `2px solid ${excelCurrency === opt ? PALETTE.cta : PALETTE.border}`,
                                            background: excelCurrency === opt ? alpha(PALETTE.cta, 0.08) : 'transparent',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            '&:hover': {
                                                borderColor: excelCurrency === opt ? PALETTE.cta : PALETTE.borderHover,
                                            },
                                        }}
                                    >
                                        <Radio
                                            checked={excelCurrency === opt}
                                            size="small"
                                            sx={{
                                                p: 0,
                                                color: PALETTE.textDim,
                                                '&.Mui-checked': { color: PALETTE.cta },
                                            }}
                                        />
                                        <Typography sx={{
                                            fontFamily: 'Vazir, sans-serif',
                                            fontWeight: 600,
                                            color: excelCurrency === opt ? PALETTE.cta : PALETTE.text,
                                            fontSize: '0.88rem',
                                        }}>
                                            {opt === 'toman' ? 'تومان' : 'ریال'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<SaveIcon />}
                            onClick={handleSaveSystemSettings}
                            disabled={loading}
                            sx={{
                                fontFamily: 'Vazir, sans-serif',
                                height: 48,
                                borderRadius: '12px',
                                fontWeight: 600,
                                background: `linear-gradient(135deg, ${PALETTE.cta} 0%, #16A34A 100%)`,
                                boxShadow: `0 4px 14px ${alpha(PALETTE.cta, 0.3)}`,
                                textTransform: 'none',
                                '&:hover': {
                                    boxShadow: `0 6px 20px ${alpha(PALETTE.cta, 0.4)}`,
                                },
                            }}
                        >
                            ذخیره تنظیمات
                        </Button>
                    </Box>
                </TabPanel>

                {/* Tab 3: Calendar */}
                <TabPanel value={tabValue} index={3}>
                    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                        <Box sx={{
                            p: 4,
                            borderRadius: '14px',
                            border: `1px solid ${PALETTE.border}`,
                            background: alpha(PALETTE.surfaceAlt, 0.3),
                            textAlign: 'center',
                        }}>
                            <Box sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '18px',
                                background: alpha(PALETTE.warning, 0.12),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3,
                            }}>
                                <CalendarIcon sx={{ fontSize: 32, color: PALETTE.warning }} />
                            </Box>

                            <Typography sx={{
                                fontFamily: 'Vazir, sans-serif',
                                fontWeight: 700,
                                fontSize: '1.05rem',
                                color: PALETTE.text,
                                mb: 1,
                            }}>
                                بروزرسانی تقویم مناسبت‌ها
                            </Typography>
                            <Typography sx={{
                                fontFamily: 'Vazir, sans-serif',
                                fontSize: '0.82rem',
                                color: PALETTE.textDim,
                                mb: 4,
                                lineHeight: 2,
                                maxWidth: 440,
                                mx: 'auto',
                            }}>
                                فایل تقویم با فرمت <span dir="ltr" style={{ color: PALETTE.info }}>.ics</span> (خروجی از Google Calendar یا سایر نرم‌افزارها) را آپلود کنید تا مناسبت‌ها در کل سیستم برای تمامی سازمان‌ها و روزها به‌روزرسانی شود.
                            </Typography>

                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<PhotoCameraIcon />}
                                    disabled={uploading}
                                    sx={{
                                        fontFamily: 'Vazir, sans-serif',
                                        fontWeight: 600,
                                        height: 48,
                                        borderRadius: '12px',
                                        borderColor: PALETTE.warning,
                                        color: PALETTE.warning,
                                        textTransform: 'none',
                                        px: 3,
                                        '&:hover': {
                                            borderColor: PALETTE.warning,
                                            background: alpha(PALETTE.warning, 0.08),
                                        },
                                    }}
                                >
                                    انتخاب فایل تقویم (.ics)
                                    <input
                                        type="file"
                                        hidden
                                        accept=".ics"
                                        onChange={async (e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                const file = e.target.files[0];
                                                setUploading(true);
                                                try {
                                                    const formData = new FormData();
                                                    formData.append('file', file);
                                                    const res = await superAdminAPI.uploadHolidays(formData);
                                                    setSuccess(res.message + ` (${res.count} روز ثبت شد)`);
                                                } catch (err: any) {
                                                    setError(err.message || 'خطا در آپلود فایل تقویم');
                                                } finally {
                                                    setUploading(false);
                                                    e.target.value = '';
                                                }
                                            }
                                        }}
                                    />
                                </Button>
                                {uploading && <CircularProgress size={24} sx={{ color: PALETTE.warning, alignSelf: 'center' }} />}
                            </Box>
                        </Box>
                    </Box>
                </TabPanel>
            </Card>

            {/* Manager Dialog - Dark Theme */}
            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        background: PALETTE.surface,
                        border: `1px solid ${PALETTE.border}`,
                        borderRadius: '16px',
                        color: PALETTE.text,
                        boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                    },
                }}
            >
                <DialogTitle sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 700,
                    borderBottom: `1px solid ${PALETTE.border}`,
                    pb: 2,
                }}>
                    {editingManager ? 'ویرایش مدیر ارشد' : 'افزودن مدیر جدید'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ position: 'relative' }}>
                            <Avatar
                                src={getImageUrl(formData.avatarUrl)}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    mb: 1,
                                    border: `2px solid ${alpha(PALETTE.accent, 0.3)}`,
                                    boxShadow: `0 0 16px ${alpha(PALETTE.accent, 0.12)}`,
                                }}
                            >
                                <PersonIcon sx={{ fontSize: 40, color: PALETTE.textDim }} />
                            </Avatar>
                            {editingManager && (
                                <IconButton
                                    component="label"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: -10,
                                        width: 28,
                                        height: 28,
                                        background: `linear-gradient(135deg, ${PALETTE.accent} 0%, #6D28D9 100%)`,
                                        color: 'white',
                                        '&:hover': { background: `linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)` },
                                    }}
                                    disabled={uploading}
                                >
                                    <input type="file" hidden accept="image/*" onChange={(e) => handleAvatarSelect(e, 'manager')} />
                                    <PhotoCameraIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            )}
                        </Box>
                        {uploading && croppingTarget === 'manager' && <CircularProgress size={16} sx={{ mt: 1, color: PALETTE.cta }} />}
                    </Box>
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            label="نام"
                            fullWidth
                            sx={{ ...darkTextFieldSx, mb: 2 }}
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        />
                        <TextField
                            label="نام خانوادگی"
                            fullWidth
                            sx={{ ...darkTextFieldSx, mb: 2 }}
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        />
                        <TextField
                            label="شماره موبایل"
                            fullWidth
                            sx={{ ...darkTextFieldSx, mb: 2 }}
                            value={formData.mobile}
                            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                            dir="ltr"
                            autoComplete="off"
                        />
                        <TextField
                            label="رمز عبور"
                            type={showAdminPassword ? 'text' : 'password'}
                            fullWidth
                            sx={{ ...darkTextFieldSx, mb: 2 }}
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            autoComplete="new-password"
                            helperText={editingManager ? 'فقط در صورت تمایل به تغییر وارد کنید' : ''}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="نمایش رمز عبور"
                                            onClick={() => setShowAdminPassword(!showAdminPassword)}
                                            edge="end"
                                            size="small"
                                            sx={{ color: PALETTE.textDim }}
                                        >
                                            {showAdminPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: PALETTE.cta,
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: PALETTE.cta,
                                        },
                                    }}
                                />
                            }
                            label="وضعیت فعال"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontFamily: 'Vazir, sans-serif',
                                    color: PALETTE.text,
                                },
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${PALETTE.border}`, gap: 1 }}>
                    <Button
                        onClick={() => setOpenModal(false)}
                        sx={{
                            fontFamily: 'Vazir, sans-serif',
                            color: PALETTE.textDim,
                            borderRadius: '10px',
                            px: 2.5,
                            '&:hover': { background: alpha(PALETTE.text, 0.05) },
                        }}
                    >
                        انصراف
                    </Button>
                    <Button
                        onClick={handleSaveManager}
                        variant="contained"
                        sx={{
                            fontFamily: 'Vazir, sans-serif',
                            fontWeight: 600,
                            borderRadius: '10px',
                            px: 3,
                            background: `linear-gradient(135deg, ${PALETTE.cta} 0%, #16A34A 100%)`,
                            boxShadow: `0 4px 14px ${alpha(PALETTE.cta, 0.3)}`,
                            '&:hover': {
                                boxShadow: `0 6px 20px ${alpha(PALETTE.cta, 0.4)}`,
                            },
                        }}
                    >
                        تایید و ذخیره
                    </Button>
                </DialogActions>
            </Dialog>

            <ImageCrop
                show={showCrop}
                onClose={() => setShowCrop(false)}
                onCropComplete={onCropComplete}
                aspect={1}
                initialFile={avatarFile}
            />

            <Snackbar
                open={!!success}
                autoHideDuration={4000}
                onClose={() => setSuccess('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSuccess('')}
                    severity="success"
                    sx={{
                        width: '100%',
                        fontFamily: 'Vazir, sans-serif',
                        background: alpha(PALETTE.cta, 0.15),
                        color: PALETTE.cta,
                        borderRadius: '12px',
                        border: `1px solid ${alpha(PALETTE.cta, 0.2)}`,
                        '& .MuiAlert-icon': { color: PALETTE.cta },
                    }}
                >
                    {success}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!error}
                autoHideDuration={4000}
                onClose={() => setError('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setError('')}
                    severity="error"
                    sx={{
                        width: '100%',
                        fontFamily: 'Vazir, sans-serif',
                        background: alpha(PALETTE.error, 0.15),
                        color: PALETTE.error,
                        borderRadius: '12px',
                        border: `1px solid ${alpha(PALETTE.error, 0.2)}`,
                        '& .MuiAlert-icon': { color: PALETTE.error },
                    }}
                >
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SuperAdminSettings;
